import type {
  IntegrationPlatform,
  IntegrationPrice,
  MetricPrice,
  PriceList,
  SupplierProfile,
} from '../api/admin/types';
import { INTEGRATION_PLATFORMS } from '../api/shared';

import { moneyInputValue, parseMoneyInput, parseWholeNumber } from './parse';

/** The supplier as the form edits it — every field text, the VAT rate in percent. */
export type SupplierDraft = Omit<
  SupplierProfile,
  | 'vatRateBasisPoints'
  | 'dueDays'
  | 'vatNumber'
  | 'email'
  | 'phone'
  | 'web'
  | 'bankName'
  | 'swift'
  | 'issuedBy'
> & {
  vatRate: string;
  dueDays: string;
  vatNumber: string;
  email: string;
  phone: string;
  web: string;
  bankName: string;
  swift: string;
  issuedBy: string;
};

export type MetricPriceDraft = { unitPrice: string; unitSize: string; unlimitedPrice: string };

export type PriceListDraft = {
  base: string;
  companies: MetricPriceDraft;
  subUsers: MetricPriceDraft;
  products: MetricPriceDraft;
  totalStock: MetricPriceDraft;
  /** Monthly price per linked shop, per platform, in euros. */
  linkPrices: Record<IntegrationPlatform, string>;
};

export type PriceMetric = 'companies' | 'subUsers' | 'products' | 'totalStock';

export const PRICE_METRICS: Array<PriceMetric> = ['companies', 'subUsers', 'products', 'totalStock'];

const text = (value: string | null): string => value ?? '';

const optional = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** "23" or "21,5" percent to basis points; anything else is unreadable. */
export const parseRate = (value: string): number | undefined => {
  const normalised = value.trim().replace(',', '.');

  if (!/^\d{1,3}(\.\d{1,2})?$/.test(normalised)) {
    return undefined;
  }

  const basisPoints = Math.round(Number(normalised) * 100);

  return basisPoints <= 10_000 ? basisPoints : undefined;
};

export const supplierDraftFrom = (profile: SupplierProfile | null): SupplierDraft => ({
  name: profile?.name ?? '',
  street: profile?.street ?? '',
  city: profile?.city ?? '',
  postalCode: profile?.postalCode ?? '',
  country: profile?.country ?? 'SK',
  registrationNumber: profile?.registrationNumber ?? '',
  taxNumber: profile?.taxNumber ?? '',
  vatNumber: text(profile?.vatNumber ?? null),
  vatPayer: profile?.vatPayer ?? false,
  vatRate: profile ? String(profile.vatRateBasisPoints / 100).replace('.', ',') : '23',
  registerEntry: profile?.registerEntry ?? '',
  email: text(profile?.email ?? null),
  phone: text(profile?.phone ?? null),
  web: text(profile?.web ?? null),
  bankName: text(profile?.bankName ?? null),
  iban: profile?.iban ?? '',
  swift: text(profile?.swift ?? null),
  proformaPrefix: profile?.proformaPrefix ?? 'ZF',
  invoicePrefix: profile?.invoicePrefix ?? '',
  dueDays: String(profile?.dueDays ?? 14),
  issuedBy: text(profile?.issuedBy ?? null),
});

/** The profile the API takes, or `null` when a number cannot be read — the API checks the rest. */
export const supplierInputFrom = (draft: SupplierDraft): SupplierProfile | null => {
  const vatRateBasisPoints = parseRate(draft.vatRate);
  const dueDays = parseWholeNumber(draft.dueDays);

  if (vatRateBasisPoints === undefined || dueDays === undefined) {
    return null;
  }

  return {
    name: draft.name.trim(),
    street: draft.street.trim(),
    city: draft.city.trim(),
    postalCode: draft.postalCode.trim(),
    country: draft.country.trim().toUpperCase(),
    registrationNumber: draft.registrationNumber.trim(),
    taxNumber: draft.taxNumber.trim(),
    vatNumber: optional(draft.vatNumber),
    vatPayer: draft.vatPayer,
    vatRateBasisPoints,
    registerEntry: draft.registerEntry.trim(),
    email: optional(draft.email),
    phone: optional(draft.phone),
    web: optional(draft.web),
    bankName: optional(draft.bankName),
    iban: draft.iban.trim(),
    swift: optional(draft.swift),
    proformaPrefix: draft.proformaPrefix.trim(),
    invoicePrefix: draft.invoicePrefix.trim(),
    dueDays,
    issuedBy: optional(draft.issuedBy),
  };
};

const metricDraft = (price: MetricPrice | undefined, defaultSize: number): MetricPriceDraft => ({
  unitPrice: price ? moneyInputValue(price.unitPriceMinor) : '0.00',
  unitSize: String(price?.unitSize ?? defaultSize),
  unlimitedPrice: price ? moneyInputValue(price.unlimitedPriceMinor) : '0.00',
});

export const priceListDraftFrom = (priceList: PriceList | null): PriceListDraft => ({
  base: priceList ? moneyInputValue(priceList.baseMonthlyMinor) : '0.00',
  companies: metricDraft(priceList?.companies, 1),
  subUsers: metricDraft(priceList?.subUsers, 1),
  products: metricDraft(priceList?.products, 100),
  totalStock: metricDraft(priceList?.totalStock, 1000),
  linkPrices: Object.fromEntries(
    INTEGRATION_PLATFORMS.map((platform) => [
      platform,
      moneyInputValue(
        priceList?.integrations?.find((item) => item.platform === platform)?.linkMonthlyMinor ?? 0,
      ),
    ]),
  ) as Record<IntegrationPlatform, string>,
});

const metricInput = (draft: MetricPriceDraft): MetricPrice | null => {
  const unitPriceMinor = parseMoneyInput(draft.unitPrice);
  const unlimitedPriceMinor = parseMoneyInput(draft.unlimitedPrice);
  const unitSize = parseWholeNumber(draft.unitSize);

  return typeof unitPriceMinor === 'number' &&
    typeof unlimitedPriceMinor === 'number' &&
    unitSize !== undefined &&
    unitSize >= 1
    ? { unitPriceMinor, unitSize, unlimitedPriceMinor }
    : null;
};

/** Every price in euros with up to two decimals, every block size a whole number from one. */
export const priceListInputFrom = (draft: PriceListDraft): PriceList | null => {
  const baseMonthlyMinor = parseMoneyInput(draft.base);
  const metrics = PRICE_METRICS.map((metric) => metricInput(draft[metric]));
  const linkPrices = INTEGRATION_PLATFORMS.map((platform) => ({
    platform,
    linkMonthlyMinor: parseMoneyInput(draft.linkPrices[platform] ?? ''),
  }));

  if (
    typeof baseMonthlyMinor !== 'number' ||
    linkPrices.some((price) => typeof price.linkMonthlyMinor !== 'number') ||
    metrics.some((metric) => metric === null)
  ) {
    return null;
  }

  const [companies, subUsers, products, totalStock] = metrics as Array<MetricPrice>;

  return {
    baseMonthlyMinor,
    companies,
    subUsers,
    products,
    totalStock,
    integrations: linkPrices as Array<IntegrationPrice>,
  };
};
